/**
 * Which tablet the guest will sign on.
 *
 * The tablet belongs to the staff workflow, so the choice is the desk's: its
 * own tablet by default, or an unassigned shared one. Only tablets that are
 * online and can take the document are listed; other desks' tablets and
 * sleeping shared ones are never shown. When
 * none can, the desk is told why its own or shared tablets are unavailable
 * ("Not paired yet", "Showing another document"). The server applies the same
 * rules; this only reflects them.
 */

import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Tablet } from 'lucide-react'
import { api } from '@/lib/api'
import type { DeviceOption } from '@/lib/types'

export function useDeviceOptions() {
  return useQuery({
    queryKey: ['sessions', 'device-options'],
    queryFn: () => api.get<DeviceOption[]>('/sessions/device-options'),
    // Tablets go online, offline and busy on their own; keep the list honest.
    refetchInterval: 15000,
  })
}

/**
 * The tablets worth offering: the ones online and free right now. An offline
 * tablet is only offered when it is this desk's own and nothing is online -
 * it is probably asleep, and it picks the document up when it wakes.
 */
export function availableDevices(options: DeviceOption[] | undefined): DeviceOption[] {
  const selectable = (options ?? []).filter((o) => o.selectable)
  const online = selectable.filter((o) => o.online)
  return online.length ? online : selectable.filter((o) => o.is_desk_default)
}

/** The option to preselect: this desk's own tablet, else any free one. */
export function defaultDevice(options: DeviceOption[] | undefined): DeviceOption | undefined {
  const available = availableDevices(options)
  return available.find((o) => o.is_desk_default) ?? available[0]
}

/** Bound to some other desk - none of this desk's business, so never listed. */
function isOtherDesks(option: DeviceOption): boolean {
  return !option.is_desk_default && option.desk_id != null
}

function describe(option: DeviceOption): string {
  const where = option.is_desk_default
    ? 'this desk'
    : option.desk_name
      ? option.desk_name
      : 'shared'
  const state = option.reason ?? (option.online ? 'online' : 'offline - will get it on wake')
  return `${option.name} (${where}) · ${state}`
}

export function DevicePicker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (deviceId: string | null) => void
}) {
  const options = useDeviceOptions()

  // Preselect once the list arrives, and re-pick if the chosen tablet stops
  // being available (it went busy with another desk's document, say).
  useEffect(() => {
    const list = options.data
    if (!list) return
    if (!availableDevices(list).some((o) => o.id === value)) {
      onChange(defaultDevice(list)?.id ?? null)
    }
  }, [options.data, value, onChange])

  if (options.isLoading) return null
  const list = options.data ?? []
  const available = availableDevices(list)
  if (!available.length) {
    const ours = list.filter((o) => !isOtherDesks(o))
    return (
      <p className="text-[12px] text-[var(--warning)]" role="status">
        No tablet can take this document right now
        {ours.length
          ? ` - ${ours.map((o) => `${o.name}: ${o.reason ?? 'unavailable'}`).join('; ')}`
          : ''}
        .
      </p>
    )
  }

  return (
    <label className="flex min-w-0 items-center gap-2 text-[13px] text-[var(--text-muted)]">
      <Tablet className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">Tablet</span>
      <select
        aria-label="Send to which tablet"
        className="h-9 min-w-0 flex-1 rounded-[10px] border border-[var(--border-strong)] bg-[var(--field)] text-[var(--text)] transition-[border-color,box-shadow] focus:border-[var(--brand)] focus:shadow-[0_0_0_3px_var(--focus-halo)] focus-visible:outline-none px-2 text-[13px] text-[var(--text)]"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
      >
        {available.map((option) => (
          <option key={option.id} value={option.id}>
            {describe(option)}
          </option>
        ))}
      </select>
    </label>
  )
}
